import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MemberTitleClient, MemberTitleDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberTitleClient(API_BASE_URL, axiosInstance);

// Query hook - Get all member titles for dropdowns (cached forever)
export const useMemberTitles = () => {
  return useQuery({
    queryKey: ['memberTitles'],
    queryFn: async () => {
      const response = await apiClient.searchMemberTitles({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useMemberTitleSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchMemberTitles({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single member title by ID
export const useMemberTitleById = (id: string) => {
  return useQuery({
    queryKey: ['memberTitles', id],
    queryFn: async () => {
      const response = await apiClient.getMemberTitleById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create member title
export const useCreateMemberTitle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: MemberTitleDTO) => {
      await apiClient.createMemberTitle(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberTitles'] });
    },
  });
};

// Mutation hook - Update member title
export const useUpdateMemberTitle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: MemberTitleDTO }) => {
      await apiClient.updateMemberTitle(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberTitles'] });
    },
  });
};

// Mutation hook - Delete member title
export const useDeleteMemberTitle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteMemberTitle(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberTitles'] });
    },
  });
};